import { StorageProvider } from './storageTypes';

export class LocalStorageProvider implements StorageProvider {
  async getItem<T>(key: string): Promise<T | null> {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error(`Error getting item from localStorage: ${key}`, error);
      return null;
    }
  }

  async setItem<T>(key: string, value: T): Promise<boolean> {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error(`Error setting item in localStorage: ${key}`, error);
      return false;
    }
  }

  async removeItem(key: string): Promise<boolean> {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Error removing item from localStorage: ${key}`, error);
      return false;
    }
  }

  async clear(): Promise<boolean> {
    try {
      // Clear only GetDeleto specific keys to avoid affecting other apps
      const keysToRemove = Object.keys(localStorage).filter(key => 
        key.startsWith('deleto') || 
        key.startsWith('getdeleto-') ||
        key === 'inactivityTimeout' ||
        key === 'hideInactivitySeconds' ||
        key === 'hasVisitedBefore'
      );
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      return true;
    } catch (error) {
      console.error('Error clearing localStorage', error);
      return false;
    }
  }

  // Additional localStorage specific methods
  getStorageInfo(): { used: string; available: string; percentage: number } {
    try {
      let totalSize = 0;
      for (let key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          totalSize += localStorage[key].length + key.length;
        }
      }
      
      const maxSize = 5 * 1024 * 1024; // 5MB typical localStorage limit
      
      return {
        used: this.formatBytes(totalSize),
        available: this.formatBytes(maxSize - totalSize),
        percentage: Math.round((totalSize / maxSize) * 100)
      };
    } catch (error) {
      console.error('Error calculating storage info:', error);
      return { used: 'Unknown', available: 'Unknown', percentage: 0 };
    }
  }

  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
} 